import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { RouteData, Address } from '../types/index';

export function generateRoutePDF(routeData: RouteData): void {
  try {
    console.log('generateRoutePDF called', routeData);
    
    // Validate input data
    if (!routeData) {
      throw new Error('No route data provided');
    }
    
    if (!routeData.selectedAddresses || routeData.selectedAddresses.length === 0) {
      throw new Error('No selected addresses found');
    }
    
    if (!routeData.startingPoint) {
      throw new Error('No starting point provided');
    }
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let yPosition = margin;

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Route Plan', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Date and time
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const now = new Date();
    doc.text(`Generated: ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`, margin, yPosition);
    yPosition += 12;

    // Route summary
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Route Summary', margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Distance: ${routeData.totalDistance || 'N/A'}`, margin, yPosition);
    yPosition += 6;
    doc.text(`Estimated Duration: ${routeData.totalDuration || 'N/A'}`, margin, yPosition);
    yPosition += 6;
    doc.text(`Number of Stops: ${routeData.selectedAddresses.length}`, margin, yPosition);
    yPosition += 12;

    // Starting point
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Starting Point', margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const startingAddress = routeData.startingPoint.address || 'Not specified';
    doc.text(startingAddress, margin, yPosition);
    yPosition += 15;

    // Route stops table header
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Route Stops', margin, yPosition);
    yPosition += 6;

    // Prepare table data - use the order of selected addresses
    // Since we don't have waypoint optimization data, we'll use the current order
    const orderedAddresses = routeData.selectedAddresses.filter(addr => addr.isSelected);
    
    const tableBody = orderedAddresses.map((address: Address, idx: number) => [
      `${idx + 1}`,
      (address.businessName && address.businessName.length > 0) ? address.businessName : '-',
      (address.address && address.address.length > 0) ? address.address : '-'
    ]);

    // Add return to start row
    tableBody.push([
      `${orderedAddresses.length + 1}`,
      'Return to Start',
      startingAddress
    ]);

    console.log('Creating table with body:', tableBody);
    
    // Use autoTable for the stops table
    autoTable(doc, {
      head: [['Stop', 'Business Name', 'Address']],
      body: tableBody,
      startY: yPosition,
      margin: { left: margin, right: margin },
      styles: { 
        font: 'helvetica', 
        fontSize: 9, 
        cellPadding: 2,
        overflow: 'linebreak'
      },
      headStyles: { 
        fillColor: [240, 240, 240], 
        textColor: 20, 
        fontStyle: 'bold' 
      },
      alternateRowStyles: { 
        fillColor: [248, 248, 248] 
      },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center' }, // Stop number
        1: { cellWidth: 50 }, // Business Name
        2: { cellWidth: 100 } // Address
      },
      didDrawPage: (data) => {
        // Footer
        const pageCount = doc.getNumberOfPages();
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.getHeight();
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Page ${doc.getCurrentPageInfo().pageNumber} of ${pageCount} - Generated by Address Mapping & Route Planner`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }
    });

    // Add route instructions if available
    if (routeData.route && routeData.route.instructions && routeData.route.instructions.length > 0) {
      const finalY = (doc as any).lastAutoTable.finalY || yPosition + 50;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Route Instructions', margin, finalY + 15);
      
      // Add instructions as a simple list
      const instructionText = routeData.route.instructions.slice(0, 10).join('\n• ');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      
      const instructionLines = doc.splitTextToSize(`• ${instructionText}`, pageWidth - 2 * margin);
      doc.text(instructionLines, margin, finalY + 25);
    }

    console.log('PDF generation completed successfully');

    // Save the PDF
    const fileName = `route-plan-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    
    // Show user-friendly error message
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    alert(`Failed to generate PDF: ${errorMessage}\n\nPlease check the console for details.`);
    
    // Re-throw for handling by calling code
    throw error;
  }
}

// Helper function to generate a simple route summary PDF without route optimization
export function generateSimpleRoutePDF(
  startingPoint: { address: string },
  selectedAddresses: Address[]
): void {
  try {
    if (!selectedAddresses || selectedAddresses.length === 0) {
      throw new Error('No addresses selected for PDF generation');
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let yPosition = margin;

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Address List', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Date and time
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const now = new Date();
    doc.text(`Generated: ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`, margin, yPosition);
    yPosition += 12;

    // Starting point
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Starting Point', margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(startingPoint.address || 'Not specified', margin, yPosition);
    yPosition += 15;

    // Address list
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Selected Addresses (${selectedAddresses.length})`, margin, yPosition);
    yPosition += 6;

    // Prepare table data
    const tableBody = selectedAddresses.map((address: Address, idx: number) => [
      `${idx + 1}`,
      (address.businessName && address.businessName.length > 0) ? address.businessName : '-',
      (address.address && address.address.length > 0) ? address.address : '-'
    ]);

    // Create table
    autoTable(doc, {
      head: [['#', 'Business Name', 'Address']],
      body: tableBody,
      startY: yPosition,
      margin: { left: margin, right: margin },
      styles: { 
        font: 'helvetica', 
        fontSize: 9, 
        cellPadding: 2,
        overflow: 'linebreak'
      },
      headStyles: { 
        fillColor: [240, 240, 240], 
        textColor: 20, 
        fontStyle: 'bold' 
      },
      alternateRowStyles: { 
        fillColor: [248, 248, 248] 
      },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center' },
        1: { cellWidth: 50 },
        2: { cellWidth: 100 }
      }
    });

    // Save the PDF
    const fileName = `address-list-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
  } catch (error) {
    console.error('Error generating simple PDF:', error);
    alert(`Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    throw error;
  }
}